import { SkyDataGrid, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { AUDIT_REPORTS, AUDIT_REPORT_IDS } from "@shared/types/auditReports";
import type { AuditView } from "@/hooks/use-audit";

/**
 * The seven saved review reports (canvas 09 block 02; SEC-16).
 *
 * A report is a CLOSED action set, not a filter the reader assembles, so the
 * evidence is reproducible: "the grants report for March" means the same rows to
 * the auditor as it did to the reviewer. The legend beside the picker says what
 * each one covers and which actions it selects, so nobody has to guess whether
 * the report they opened is the one the auditor asked for.
 *
 * Opening one is itself audited as `audit.review` by the route — which is why the
 * picker changes the QUERY rather than filtering rows already on screen.
 */

interface LegendRow {
  id: string;
  report: string;
  covers: string;
  actions: string;
}

const COVERS: Record<(typeof AUDIT_REPORT_IDS)[number], string> = {
  "sign-ins": "Who signed in, who failed, and how often.",
  // #1090: the legend cell truncates, so the denials explanation lives in the report's banner.
  denials: "Requests the server refused, by actor and permission.",
  grants: "Every grant, revocation and reactivation, with break-glass grants first.",
  "destination-changes": "Every binding read and change.",
  "secret-writes": "Every vault write, by reference. Values never appear.",
  "force-resets": "Runs an administrator force-reset out of a stuck state.",
  exports: "Every CSV export of the trail, including this one.",
};

const LEGEND_ROWS: LegendRow[] = AUDIT_REPORT_IDS.map((id) => ({
  id,
  report: AUDIT_REPORTS[id].label,
  covers: COVERS[id],
  actions: AUDIT_REPORTS[id].actions.join(", "),
}));

const LEGEND_COLUMNS: SkyDataGridColumn<LegendRow>[] = [
  { key: "report", header: "Saved report", width: "26%", sortable: false },
  { key: "covers", header: "What it lists", width: "40%", sortable: false },
  {
    key: "actions",
    header: "Actions filtered",
    width: "34%",
    sortable: false,
    renderCell: (_value, row) => (
      <SkyText size={200} font="numeric">
        {row.actions}
      </SkyText>
    ),
  },
];

export function AuditReportPicker({
  view,
  onChange,
  showLegend,
}: {
  view: AuditView;
  onChange: (view: AuditView) => void;
  showLegend: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-80">
          <SkyDropdown
            label="Report"
            testId="audit-report"
            selectedOptions={[view]}
            listboxMaxWidth="420px"
            options={[
              { value: "all", text: "All events: the unfiltered trail" },
              ...AUDIT_REPORT_IDS.map((id) => ({
                value: id,
                text: AUDIT_REPORTS[id].label,
              })),
            ]}
            onOptionSelect={(values) => onChange((values[0] as AuditView) ?? "all")}
          />
        </div>
        <SkyText as="p" size={200} color="muted">
          Opening a report is itself audited as audit.review, and exporting one as history.export.
        </SkyText>
      </div>

      {showLegend ? (
        <SkyDataGrid<LegendRow>
          data={LEGEND_ROWS}
          columns={LEGEND_COLUMNS}
          getRowId={(row) => row.id}
          testId="audit-report-legend"
        />
      ) : null}
    </div>
  );
}
